// src/pages/admin/AdminAnalytics.js
// Sponsor analytics — impressions, clicks, and town activity context.
// Admin-facing for now: pull real numbers to share with sponsors manually.
// A sponsor-facing self-service portal is Phase 2B, not this build.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSponsorAnalytics } from '../../lib/db';
import { format } from 'date-fns';
import { PageBack } from '../../components/shared/PageBack';

const TIER_LABELS = {
  founding: 'Founding Partner',
  gold: 'Community Partner',
  supporter: 'Community Supporter',
};

const AdminAnalytics = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [townFilter, setTownFilter] = useState('all');

  useEffect(() => {
    getSponsorAnalytics().then(data => {
      setSponsors(data.sort((a, b) => (b.impressions || 0) - (a.impressions || 0)));
      setLoading(false);
    });
  }, []);

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try { return format(ts?.toDate ? ts.toDate() : new Date(ts), 'd MMM yyyy'); }
    catch { return '—'; }
  };

  const towns = [...new Set(sponsors.map(s => s.townId).filter(Boolean))];
  const filtered = townFilter === 'all' ? sponsors : sponsors.filter(s => s.townId === townFilter);

  const totals = filtered.reduce((acc, s) => ({
    impressions: acc.impressions + (s.impressions || 0),
    clicks: acc.clicks + (s.clicks || 0),
  }), { impressions: 0, clicks: 0 });
  const overallCtr = totals.impressions ? Math.round((totals.clicks / totals.impressions) * 1000) / 10 : 0;

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;

  return (
    <div style={s.page}>
      <button onClick={() => navigate('/admin')} style={s.back}>
        <div style={s.backCircle}>←</div>
        <span style={s.backLbl}>Admin</span>
      </button>
      <div style={s.h1}>Sponsor Analytics</div>
      <p style={s.subtitle}>Impressions and clicks since each sponsor went active. Share these with sponsors on request or renewal.</p>

      {towns.length > 1 && (
        <select style={s.filterSelect} value={townFilter} onChange={e => setTownFilter(e.target.value)}>
          <option value="all">All towns</option>
          {towns.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      )}

      {/* Overall totals */}
      <div style={s.totalsRow}>
        <div style={s.totalCard}>
          <div style={s.totalNum}>{totals.impressions.toLocaleString()}</div>
          <div style={s.totalLbl}>Total impressions</div>
        </div>
        <div style={s.totalCard}>
          <div style={s.totalNum}>{totals.clicks.toLocaleString()}</div>
          <div style={s.totalLbl}>Total clicks</div>
        </div>
        <div style={s.totalCard}>
          <div style={s.totalNum}>{overallCtr}%</div>
          <div style={s.totalLbl}>Avg. CTR</div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No sponsors yet.</div>
      ) : filtered.map(sp => (
        <div key={sp.id} style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={s.sponsorName}>{sp.name}</div>
              <div style={s.meta}>{TIER_LABELS[sp.tier] || sp.tier} · {sp.townId} · sponsor since {fmtDate(sp.createdAt)}</div>
            </div>
            <span style={{ ...s.badge, background: sp.active ? 'var(--sage-pale)' : '#F0F0EE', color: sp.active ? 'var(--sage-deep)' : 'var(--muted)' }}>
              {sp.active ? 'Active' : 'Inactive'}
            </span>
          </div>

          <div style={s.statsRow}>
            <div style={s.statBox}>
              <div style={s.statNum}>{(sp.impressions || 0).toLocaleString()}</div>
              <div style={s.statLbl}>Impressions</div>
            </div>
            <div style={s.statBox}>
              <div style={s.statNum}>{(sp.clicks || 0).toLocaleString()}</div>
              <div style={s.statLbl}>Clicks</div>
            </div>
            <div style={s.statBox}>
              <div style={s.statNum}>{sp.ctr}%</div>
              <div style={s.statLbl}>CTR</div>
            </div>
          </div>

          <div style={s.contextRow}>
            <span>📅 {sp.townStats.liveEvents} live events</span>
            <span>❤️ {sp.townStats.interests} RSVPs</span>
            <span>👀 {sp.townStats.eventViews} event views</span>
          </div>
        </div>
      ))}
      <PageBack position="bottom" fallbackPath="/admin" />
    </div>
  );
};

const s = {
  page: { padding: '52px 20px 40px', background: 'var(--cream)', minHeight: '100vh' },
  back: { display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20 },
  backCircle: { width: 34, height: 34, background: 'var(--white)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: 'var(--sage-dark)', boxShadow: 'var(--shadow-sm)' },
  backLbl: { fontSize: 13, color: 'var(--muted)', fontWeight: 600 },
  h1: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.5 },
  filterSelect: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '1.5px solid #C8D9C9', fontSize: 13, fontFamily: 'Nunito', background: 'var(--white)', marginBottom: 16, boxSizing: 'border-box' },
  totalsRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 20 },
  totalCard: { background: 'var(--white)', borderRadius: 14, padding: '14px 8px', textAlign: 'center', boxShadow: 'var(--shadow-sm)' },
  totalNum: { fontSize: 20, fontWeight: 800, color: 'var(--sage-deep)' },
  totalLbl: { fontSize: 10, color: 'var(--muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 2 },
  card: { background: 'var(--white)', borderRadius: 16, padding: 16, marginBottom: 12, boxShadow: 'var(--shadow-sm)' },
  sponsorName: { fontSize: 16, fontWeight: 800, color: 'var(--ink)' },
  meta: { fontSize: 12, color: 'var(--muted)', fontWeight: 600, marginTop: 2 },
  badge: { fontSize: 10, padding: '3px 9px', borderRadius: 100, fontWeight: 700, flexShrink: 0 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, background: 'var(--sage-pale)', borderRadius: 12, padding: '10px 6px', marginBottom: 10 },
  statBox: { textAlign: 'center' },
  statNum: { fontSize: 16, fontWeight: 800, color: 'var(--sage-dark)' },
  statLbl: { fontSize: 10, color: 'var(--sage-dark)', fontWeight: 600, opacity: 0.8 },
  contextRow: { display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)', fontWeight: 600 },
};

export default AdminAnalytics;
